import { useState } from 'react';
import { WorkspaceWhitelistRulesHeader } from '../WorkspaceWhitelistRulesHeader';
import { WorkspaceWhitelistRulesTable } from '../WorkspaceWhitelistRulesTable';
import type { WorkspaceRule } from '../WorkspaceWhitelistRulesTable';
import { AddWhitelistRuleModal } from '../AddWhitelistRuleModal';
import { DeleteConfirmationModal } from '@/shared/ui/DeleteConfirmationModal';
import { useUpdateWhitelist, useWorkspaceWhitelistRules } from '../../model';
import { useTableData } from '@/shared/hooks';
import { notificationService } from '@/lib/services/notification.service';
import { type ModelProviderWhitelistResponseDto } from '@inference.engine/api-client';
import { useTranslation } from 'react-i18next';

interface WorkspaceProviderWhitelistRulesProps {
  whitelistedProviders: ModelProviderWhitelistResponseDto[];
}

export const WorkspaceProviderWhitelistRules = ({
  whitelistedProviders,
}: WorkspaceProviderWhitelistRulesProps) => {
  const { t } = useTranslation('admin');

  const [isAddRuleModalOpen, setIsAddRuleModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedWorkspace, setSelectedWorkspace] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const updateWhitelistMutation = useUpdateWhitelist();
  const whitelistRules = useWorkspaceWhitelistRules(whitelistedProviders);

  const {
    paginatedData,
    totalCount,
    searchValue,
    setSearchValue,
    sortBy,
    sortDirection,
    handleSort,
    page,
    rowsPerPage,
    handlePageChange,
    handleRowsPerPageChange,
  } = useTableData<WorkspaceRule>({
    data: whitelistRules,
    searchFilter: (rule, search) => {
      const workspaceMatch = rule.workspaceName.toLowerCase().includes(search);
      const providerMatch = rule.providers.some((p) => p.name.toLowerCase().includes(search));
      return workspaceMatch || providerMatch;
    },
    initialSort: { key: 'workspaceName', direction: 'asc' },
    initialRowsPerPage: 10,
  });

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
  };

  const handleAddRuleClick = () => {
    setIsAddRuleModalOpen(true);
  };

  const handleAddRuleSave = async (providers: string[], workspaceId: string) => {
    try {
      await updateWhitelistMutation.mutateAsync({ providerIds: providers, workspaceId });
      notificationService.showSuccess(
        t('admin.workspaceProviderWhitelistRules.workspaceWhitelistRuleAdded'),
      );
      setIsAddRuleModalOpen(false);
    } catch {
      notificationService.showError(
        t('admin.workspaceProviderWhitelistRules.failedToAddWorkspaceWhitelist'),
      );
    }
  };

  const handleDeleteClick = (workspaceId: string, workspaceName: string) => {
    setSelectedWorkspace({ id: workspaceId, name: workspaceName });
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedWorkspace) return;

    try {
      // Save empty providers array to delete the rule for this workspace
      await updateWhitelistMutation.mutateAsync({
        providerIds: [],
        workspaceId: selectedWorkspace.id,
      });
      notificationService.showSuccess(
        t('admin.workspaceProviderWhitelistRules.workspaceWhitelistRuleDeleted'),
      );
      setIsDeleteModalOpen(false);
      setSelectedWorkspace(null);
    } catch {
      notificationService.showError(
        t('admin.workspaceProviderWhitelistRules.failedToDeleteWorkspaceWhitelist'),
      );
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setSelectedWorkspace(null);
  };

  return (
    <div className="flex flex-col gap-4 w-full">
      <WorkspaceWhitelistRulesHeader
        searchValue={searchValue}
        rulesCount={whitelistRules.length}
        onSearchChange={handleSearchChange}
        onAddClick={handleAddRuleClick}
      />

      <WorkspaceWhitelistRulesTable
        rules={paginatedData}
        hasSearch={searchValue.trim().length > 0}
        pagination={{
          count: totalCount,
          page,
          rowsPerPage,
          onPageChange: handlePageChange,
          onRowsPerPageChange: handleRowsPerPageChange,
        }}
        sortBy={sortBy}
        sortDirection={sortDirection}
        onSort={handleSort}
        onDelete={handleDeleteClick}
      />

      <AddWhitelistRuleModal
        open={isAddRuleModalOpen}
        onClose={() => setIsAddRuleModalOpen(false)}
        whitelistedProviders={whitelistedProviders}
        onSave={handleAddRuleSave}
        isSaving={updateWhitelistMutation.isPending}
      />

      <DeleteConfirmationModal
        open={isDeleteModalOpen}
        isDeleting={updateWhitelistMutation.isPending}
        onCancel={handleCancelDelete}
        onConfirmDelete={() => void handleConfirmDelete()}
        customModalTitle={t('admin.workspaceProviderWhitelistRules.deleteWhitelistRuleFor', {
          workspaceName: selectedWorkspace?.name ?? '',
        })}
        warningMessage={t('admin.workspaceProviderWhitelistRules.thisWillRemoveAllModel')}
      />
    </div>
  );
};
